import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Node, Edge, ReactFlowProvider } from 'reactflow';
import { ProcessingGraph } from './ProcessingGraph';
import { useWebSocket } from '../../hooks/useWebSocket';

interface CollaborativeGraphProps {
  sessionId: string;
  height?: string;
  readOnly?: boolean;
}

interface UserCursor {
  userId: string;
  username: string;
  position: { x: number; y: number };
  color: string;
}

export const CollaborativeGraph: React.FC<CollaborativeGraphProps> = ({
  sessionId,
  height = '600px',
  readOnly = false
}) => {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [userCursors, setUserCursors] = useState<UserCursor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Generate a random color for this user
  const userColor = React.useMemo(() => {
    const colors = ['#FF5722', '#9C27B0', '#3F51B5', '#009688', '#FFC107', '#795548'];
    return colors[Math.floor(Math.random() * colors.length)];
  }, []);
  
  // Get current user info
  const userId = localStorage.getItem('userId') || `user-${Date.now()}`;
  const username = localStorage.getItem('username') || 'Anonymous';
  
  // Store userId if not already stored
  useEffect(() => {
    if (!localStorage.getItem('userId')) {
      localStorage.setItem('userId', userId);
    }
  }, [userId]);
  
  // Connect to WebSocket for real-time collaboration
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsHost = window.location.host;
  const wsUrl = `${wsProtocol}//${wsHost}/ws/graph/${sessionId}`;
  
  const { 
    sendMessage, 
    lastMessage, 
    connectionStatus 
  } = useWebSocket(wsUrl);
  
  // Handle incoming WebSocket messages
  useEffect(() => {
    if (lastMessage) {
      try {
        const data = JSON.parse(lastMessage.data);
        
        switch (data.type) {
          case 'graph_update':
            // Update graph data
            setNodes(data.nodes);
            setEdges(data.edges);
            setIsLoading(false);
            break;
            
          case 'cursor_update':
            // Update user cursors
            setUserCursors(prevCursors => {
              // Remove the user's cursor if it already exists
              const filteredCursors = prevCursors.filter(
                cursor => cursor.userId !== data.cursor.userId
              );
              
              // Add the updated cursor
              return [...filteredCursors, data.cursor];
            });
            break;
            
          case 'user_joined':
            // Add notification or update user list
            console.log(`User joined: ${data.username}`);
            break;
            
          case 'user_left':
            // Remove user's cursor and update user list
            setUserCursors(prevCursors => 
              prevCursors.filter(cursor => cursor.userId !== data.userId)
            );
            console.log(`User left: ${data.username}`);
            break;
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
      }
    }
  }, [lastMessage]);
  
  // Send cursor position updates
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const position = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
    
    // Throttle cursor updates to avoid flooding the server
    if (connectionStatus === 'Open') {
      sendMessage(JSON.stringify({
        type: 'cursor_update',
        cursor: {
          userId,
          username,
          position,
          color: userColor
        }
      }));
    }
  }, [connectionStatus, sendMessage, userId, username, userColor]);
  
  // Send graph updates when nodes or edges change
  const handleGraphChange = useCallback((nodes: Node[], edges: Edge[]) => {
    if (connectionStatus === 'Open') {
      sendMessage(JSON.stringify({
        type: 'graph_update',
        nodes,
        edges
      }));
    }
  }, [connectionStatus, sendMessage]);
  
  // Render user cursors
  const renderUserCursors = () => {
    return userCursors.map(cursor => (
      <div
        key={cursor.userId}
        className="absolute pointer-events-none z-50"
        style={{
          left: cursor.position.x,
          top: cursor.position.y,
          transform: 'translate(-50%, -50%)'
        }}
      >
        <div className="flex flex-col items-center">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: cursor.color }}
          >
            <path
              d="M5.64124 16.7393L11.2168 5.4336C11.6546 4.56348 12.9073 4.6084 13.2771 5.50293L15.9206 12.2979L19.0737 13.7871C19.9268 14.1885 19.8389 15.3887 18.9385 15.6729L7.20239 19.3379C6.21191 19.6504 5.34956 18.7881 5.66206 17.7975L5.64124 16.7393Z"
              fill="currentColor"
            />
          </svg>
          <div
            className="px-2 py-1 rounded text-xs text-white"
            style={{ backgroundColor: cursor.color }}
          >
            {cursor.username}
          </div>
        </div>
      </div>
    ));
  };
  
  return (
    <div 
      className="collaborative-graph relative" 
      style={{ height }}
      onMouseMove={handleMouseMove}
    >
      {connectionStatus !== 'Open' && (
        <div className="absolute top-4 left-4 z-50 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-md">
          {connectionStatus === 'Connecting' ? 'Connecting...' : 'Disconnected'}
        </div>
      )}
      
      <ReactFlowProvider>
        <ProcessingGraph
          initialNodes={nodes}
          initialEdges={edges}
          height={height}
          readOnly={readOnly}
          showControls={true}
          showMinimap={true}
          onNodesChange={(nodes) => handleGraphChange(nodes, edges)}
          onEdgesChange={(edges) => handleGraphChange(nodes, edges)}
        />
      </ReactFlowProvider>
      
      {renderUserCursors()}
    </div>
  );
};
